import React from "react";

import { styled } from "@mui/material/styles";
import MTooltip, { tooltipClasses, TooltipProps } from "@mui/material/Tooltip";
import { colors } from "@zapalacorp/tokens";

export const DroppableAreaContainer = styled("div", {
  shouldForwardProp(propName) {
    return propName !== "isDraggingOver" && propName !== "isDraggingLeader";
  },
})<{ isDraggingOver?: boolean; isDraggingLeader?: boolean }>(
  ({ isDraggingOver, isDraggingLeader }) => ({
    position: "relative",
    padding: isDraggingOver ? (isDraggingLeader ? "30px 20px" : "60px 20px") : 0, // extends the element hitbox area
    margin: isDraggingOver ? (isDraggingLeader ? "-30px -20px" : "-60px -20px") : 0, // pulls it back so visible content stays in same position
  })
);

export const Container = styled("div", {
  shouldForwardProp(propName) {
    return (
      propName !== "isDraggingOver" &&
      propName !== "backgroundColorActive" &&
      propName !== "backgroundColor"
    );
  },
})<{ isDraggingOver?: boolean; backgroundColor?: string; backgroundColorActive?: string }>(
  ({
    isDraggingOver,
    backgroundColor = colors.white,
    backgroundColorActive = colors.success50,
  }) => ({
    borderRadius: "4px",
    background: isDraggingOver ? backgroundColorActive : backgroundColor,
    width: "100%",
    minHeight: "40px",
    display: "flex",
  })
);

export const ContainerIcon = styled("div", {
  shouldForwardProp(propName) {
    return propName !== "active";
  },
})<{ active?: boolean }>(({ active }) => ({
  display: "flex",
  alignItems: "center",
  padding: "8px 16px",
  color: active ? colors.gray600 : colors.gray400,
}));

export const ContainerItem = styled("div", {
  shouldForwardProp(propName) {
    return propName !== "disableGutters" && propName !== "active";
  },
})<{ active?: boolean; disableGutters?: boolean }>(({ active, disableGutters }) => ({
  color: active ? colors.gray600 : colors.gray400,
  padding: disableGutters ? undefined : "8px 4px ",
  textOverflow: "ellipsis",
  fontFamily: "Manrope",
  fontSize: "14px",
  fontWeight: 400,
  lineHeight: "20px",
  letterSpacing: "0.25px",
  display: "flex",
  flexDirection: "column",
  flex: 1,
  overflow: "hidden",
  gap: "4px",
}));

export const ContainerSubItem = styled("div", {
  shouldForwardProp(propName) {
    return propName !== "disableHouver" && propName !== "hasError" && propName !== "forceHouver";
  },
})<{ disableHouver?: boolean; hasError?: boolean; forceHouver?: boolean }>(
  ({ disableHouver, hasError, forceHouver }) => {
    const hoverStyle = !disableHouver
      ? {
          "&:hover": {
            backgroundColor: hasError ? "#F044381F" : "#66708514",
          },
        }
      : undefined;

    const getBackGroundColor = () => {
      if (hasError) return "#F044381F";

      if (forceHouver) return "#66708514";

      return "transparent";
    };

    return {
      width: "100%",
      display: "flex",
      alignItems: "center",
      padding: "4px 8px",
      borderRadius: "4px",
      borderStyle: "none",
      backgroundColor: getBackGroundColor(),

      color: hasError ? colors.error600 : "inherit",

      ...hoverStyle,
    };
  }
);

export const ContainerLabel = styled("div")(() => ({
  display: "flex",
  gap: "4px",
  flex: 1,

  "& strong": {
    fontWeight: 600,
  },
}));

export const Tooltip = styled(({ className, ...props }: TooltipProps) => (
  <MTooltip {...props} classes={{ popper: className }} />
))(() => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: colors.gray600,
    lineHeight: "16px",
    fontSize: "12px",
    fontStyle: "Manrope",
    letterSpacing: "0.4px",
  },
}));

export const PlaceholderContainer = styled("div", {
  shouldForwardProp(propName) {
    return (
      propName !== "isUsingPlaceholder" &&
      propName !== "canDrop" &&
      propName !== "draggedItemHeight"
    );
  },
})<{ isUsingPlaceholder?: boolean; canDrop?: boolean; draggedItemHeight?: number }>(
  ({ isUsingPlaceholder, canDrop, draggedItemHeight }) => ({
    height: isUsingPlaceholder && canDrop ? draggedItemHeight : 0,
    transition: "height 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    overflow: "hidden",
  })
);

export const PlaceholderContent = styled("div", {
  shouldForwardProp(propName) {
    return propName !== "draggedItemHeight";
  },
})<{ draggedItemHeight?: number }>(({ draggedItemHeight }) => ({
  height: draggedItemHeight || 0,
}));
